import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { api } from "@/utils/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead, { generateWebPageSchema, generateBreadcrumbSchema } from "@/components/SEOHead";
import { MapPin, ChevronRight, Users, Star, Shield, Clock, Phone } from "lucide-react";

// Service categories for linking
const SERVICES = [
  { slug: 'escort-service', name: 'Escort Service', icon: '💃' },
  { slug: 'call-girls', name: 'Call Girls', icon: '📞' },
  { slug: 'massage-service', name: 'Massage Service', icon: '💆' }
];

// Generate detailed city description for SEO
const generateCityDescription = (cityName, state) => {
  const descriptions = {
    intro: `Welcome to uplino.in - your premier destination for finding verified adult services in ${cityName}, ${state}. Whether you are visiting ${cityName} for business or leisure, our platform connects you with trusted service providers who offer premium experiences.`,
    
    services: `Discover a wide range of services in ${cityName} including professional escort services, independent call girls, and relaxing massage parlors. All our listings feature verified providers with real photos, detailed profiles, and direct contact options.`,
    
    safety: `Your safety is our top priority. All service providers on uplino.in are verified through our rigorous screening process. We ensure that every listing in ${cityName} meets our quality standards, giving you peace of mind when making your choice.`,
    
    booking: `Booking is simple and discreet. Browse through our ${cityName} listings, view photos and descriptions, and contact providers directly via WhatsApp, Telegram, or phone call. Our platform ensures complete privacy and confidentiality.`,
    
    availability: `Services in ${cityName} are available across multiple time slots - Morning (6 AM - 12 PM), Afternoon (12 PM - 6 PM), Evening (6 PM - 12 AM), and Night (12 AM - 6 AM). Many providers also offer 24-hour availability for your convenience.`,
    
    areas: `Our ${cityName} listings cover all major areas and neighborhoods. Whether you are in the city center, business district, or suburban areas, you will find service providers near your location.`
  };
  
  return descriptions;
};

// Popular areas for major cities
const CITY_AREAS = {
  'mumbai': ['Andheri', 'Bandra', 'Juhu', 'Powai', 'Colaba', 'Worli', 'Lower Parel'],
  'delhi': ['Connaught Place', 'South Delhi', 'Karol Bagh', 'Dwarka', 'Aerocity', 'Nehru Place'],
  'bangalore': ['MG Road', 'Koramangala', 'Whitefield', 'Indiranagar', 'Electronic City', 'JP Nagar'],
  'hyderabad': ['Banjara Hills', 'Jubilee Hills', 'Hitech City', 'Gachibowli', 'Secunderabad'],
  'chennai': ['T Nagar', 'Anna Nagar', 'Adyar', 'Velachery', 'OMR', 'Nungambakkam'],
  'kolkata': ['Park Street', 'Salt Lake', 'New Town', 'Ballygunge', 'Howrah'],
  'pune': ['Koregaon Park', 'Kalyani Nagar', 'Viman Nagar', 'Hinjewadi', 'Kothrud'],
  'goa': ['Calangute', 'Baga', 'Candolim', 'Anjuna', 'Panjim', 'Vagator']
};

const AllCities = () => {
  const { city } = useParams();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCityData, setSelectedCityData] = useState(null);

  useEffect(() => {
    loadCities();
  }, [city]);

  const loadCities = async () => {
    try {
      const response = await api.get("/cities");
      setCities(response.data);
      
      if (city) {
        const cityData = response.data.find(c => c.slug === city);
        setSelectedCityData(cityData);
      }
    } catch (error) {
      console.error("Failed to load cities");
    } finally {
      setLoading(false);
    }
  };

  // If specific city is selected, show city landing page
  if (city && selectedCityData) {
    const cityName = selectedCityData.name;
    const canonicalUrl = `https://uplino.in/${city}`;
    
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        generateWebPageSchema(
          `${cityName} Adult Services | Escorts, Call Girls, Massage - uplino.in`,
          `Find verified adult services in ${cityName}. Browse escorts, call girls, massage services with real photos and contact details on uplino.in`,
          canonicalUrl
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: 'https://uplino.in' },
          { name: cityName, url: canonicalUrl }
        ])
      ]
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <SEOHead
          title={`${cityName} Adult Services | Escorts, Call Girls, Massage | uplino.in`}
          description={`Discover the best adult services in ${cityName}. Verified escorts, call girls, and massage services with photos and contact details. Safe & discreet listings on uplino.in.`}
          keywords={[
            `${cityName.toLowerCase()} escorts`,
            `call girls ${cityName}`,
            `${cityName.toLowerCase()} massage`,
            `adult services ${cityName}`,
            `${cityName.toLowerCase()} escort service`,
            `${cityName.toLowerCase()} call girl`,
            `massage ${cityName.toLowerCase()}`
          ]}
          canonical={canonicalUrl}
          schema={schema}
        />
        
        <Header />
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white">Home</Link>
              </li>
              <ChevronRight className="w-4 h-4 text-gray-600" />
              <li>
                <span className="text-white font-medium">{cityName}</span>
              </li>
            </ol>
          </nav>

          {/* City Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-8 h-8 text-blue-500" />
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                Adult Services in {cityName}
              </h1>
            </div>
            <p className="text-lg text-gray-400 max-w-3xl">
              Browse verified adult service providers in {cityName}. Find escorts, call girls, and massage services 
              with real photos, reviews, and direct contact information. All listings on uplino.in are verified for your safety.
            </p>
          </header>

          {/* Services Grid */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">
              Browse Services in {cityName}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {SERVICES.map((service) => (
                <Link
                  key={service.slug}
                  to={`/${city}/${service.slug}`}
                  className="group"
                >
                  <Card className="p-8 bg-white/5 hover:bg-white/10 border-white/10 transition-all h-full">
                    <div className="text-5xl mb-4">{service.icon}</div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                      {service.name} in {cityName}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Find verified {service.name.toLowerCase()} providers in {cityName}
                    </p>
                    <span className="inline-flex items-center text-blue-500 text-sm font-medium">
                      Browse Listings <ChevronRight className="w-4 h-4 ml-1" />
                    </span>
                  </Card>
                </Link>
              ))}
            </div>
          </section>

          {/* SEO Content */}
          <section className="mb-12">
            <Card className="p-8 bg-white/5 border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">
                Why Choose uplino.in for {cityName} Adult Services?
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-400 mb-4">
                  {generateCityDescription(cityName, selectedCityData?.state || 'India').intro}
                </p>
                <p className="text-gray-400 mb-4">
                  {generateCityDescription(cityName, selectedCityData?.state || 'India').services}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div className="flex items-start gap-3">
                    <Star className="w-5 h-5 text-yellow-500 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">Verified Listings</h4>
                      <p className="text-gray-400 text-sm">All providers are verified with real photos and genuine profiles</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-blue-500 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">Direct Contact</h4>
                      <p className="text-gray-400 text-sm">Connect directly via WhatsApp, Telegram or phone call</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-green-500 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">Safe & Discreet</h4>
                      <p className="text-gray-400 text-sm">Complete privacy and confidentiality guaranteed</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-purple-500 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">24/7 Availability</h4>
                      <p className="text-gray-400 text-sm">Service providers available round the clock</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </section>

          {/* Detailed SEO Content Section */}
          <section className="mb-12 space-y-6">
            <Card className="p-6 bg-white/5 border-white/10">
              <h3 className="text-xl font-bold text-white mb-3">
                About Adult Services in {cityName}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                {generateCityDescription(cityName, selectedCityData?.state || 'India').safety}
              </p>
            </Card>

            <Card className="p-6 bg-white/5 border-white/10">
              <h3 className="text-xl font-bold text-white mb-3">
                How to Book Services in {cityName}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                {generateCityDescription(cityName, selectedCityData?.state || 'India').booking}
              </p>
            </Card>

            <Card className="p-6 bg-white/5 border-white/10">
              <h3 className="text-xl font-bold text-white mb-3">
                Service Availability in {cityName}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                {generateCityDescription(cityName, selectedCityData?.state || 'India').availability}
              </p>
            </Card>

            {/* Popular Areas - Only for major cities */}
            {CITY_AREAS[city?.toLowerCase()] && (
              <Card className="p-6 bg-white/5 border-white/10">
                <h3 className="text-xl font-bold text-white mb-3">
                  Popular Areas in {cityName}
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Find service providers in these popular {cityName} neighborhoods:
                </p>
                <div className="flex flex-wrap gap-2">
                  {CITY_AREAS[city?.toLowerCase()].map((area) => (
                    <span key={area} className="px-3 py-1 bg-white/10 text-gray-300 text-sm rounded-full">
                      {area}
                    </span>
                  ))}
                </div>
              </Card>
            )}
          </section>

          {/* Other Cities */}
          <section className="mb-12">
            <h3 className="text-xl font-bold text-white mb-4">
              Browse Other Cities
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {cities.filter(c => c.slug !== city).slice(0, 10).map((c) => (
                <Link
                  key={c.slug}
                  to={`/${c.slug}`}
                  className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-center transition-all"
                >
                  <span className="text-white text-sm">{c.name}</span>
                </Link>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    );
  }

  // All Cities Page
  const canonicalUrl = 'https://uplino.in/cities';
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      generateWebPageSchema(
        'All Cities - Adult Services in India | uplino.in',
        'Browse adult services across all major cities in India. Find escorts, call girls, massage services in Mumbai, Delhi, Bangalore, and more.',
        canonicalUrl
      ),
      {
        "@type": "ItemList",
        "name": "Cities with Adult Services",
        "numberOfItems": cities.length,
        "itemListElement": cities.map((city, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": city.name,
          "url": `https://uplino.in/${city.slug}`
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <SEOHead
        title="All Cities - Adult Services in India | uplino.in"
        description="Find adult services in all major Indian cities. Browse escorts, call girls, massage services in Mumbai, Delhi, Bangalore, Chennai, Hyderabad, Kolkata, Pune, and more."
        keywords={[
          'adult services india',
          'escorts india',
          'call girls india',
          'massage service india',
          'uplino cities'
        ]}
        canonical={canonicalUrl}
        schema={schema}
      />

      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-400 hover:text-white">Home</Link>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <li>
              <span className="text-white font-medium">All Cities</span>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Adult Services Across India
          </h1>
          <p className="text-lg text-gray-400 max-w-3xl">
            Browse verified adult service providers across all major cities in India. 
            Select your city to find escorts, call girls, and massage services near you.
          </p>
        </header>

        {/* Cities Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-white text-xl">Loading cities...</div>
          </div>
        ) : (
          <section>
            <h2 className="text-xl font-bold text-white mb-6">Select Your City</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {cities.map((city) => (
                <Link
                  key={city.slug}
                  to={`/${city.slug}`}
                  className="group"
                >
                  <Card className="p-6 bg-white/5 hover:bg-white/10 border-white/10 transition-all text-center">
                    <MapPin className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                    <h3 className="text-white font-medium group-hover:text-blue-400 transition-colors">
                      {city.name}
                    </h3>
                    <p className="text-gray-500 text-xs mt-1">{city.state}</p>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Services Overview */}
        <section className="mt-12">
          <h2 className="text-xl font-bold text-white mb-6">Services Available</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.map((service) => (
              <Card key={service.slug} className="p-6 bg-white/5 border-white/10">
                <div className="text-4xl mb-3">{service.icon}</div>
                <h3 className="text-lg font-bold text-white mb-2">{service.name}</h3>
                <p className="text-gray-400 text-sm">
                  Available in all cities listed above
                </p>
              </Card>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AllCities;
